import { MarkdownViewer } from '@/components/MarkdownViewer'
import { ScrollableContent } from '@/components/ScrollableContent'
import { MotionPlayIcon } from '@/components/icons'
import { WorkItemStatusBadge } from '@/components/projects/WorkItemStatusBadge'
import { TriggerWorkflowDialog } from '@/components/projects/workflows/TriggerWorkflowDialog'
import { TriggerWorkflowFromEntityDialog, type TriggerableDefinition } from '@/components/projects/workflows/TriggerWorkflowFromEntityDialog'
import { WorkflowHistoryTab } from '@/components/projects/workflows/WorkflowHistoryTab'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TabBar } from '@/components/TabBar'
import { useAuth, useIntent } from '@/hooks'
import { useIntentAttachments } from '@/hooks/useIntentAttachments'
import { Permissions } from '@/lib/permissions'
import type { Intent } from '@/types'
import { Loader2, Pencil, Plus } from 'lucide-react'
import { useState } from 'react'
import { IntentAttachmentsTab } from './IntentAttachmentsTab'

const FIELD_MAX_HEIGHT = 'calc(1.625em * 20 + 2rem)'

type Tab = 'overview' | 'attachments' | 'workflow-history'

const baseTabs: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'workflow-history', label: 'Workflow History' },
  { key: 'attachments', label: 'Attachments' },
]

interface IntentDetailDialogProps {
  intent: Intent | null
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onEdit?: () => void
}

export function IntentDetailDialog({ intent, projectId, open, onOpenChange, onEdit }: IntentDetailDialogProps) {
  const { hasPermission } = useAuth(projectId)
  const canEdit = hasPermission(Permissions.Project.Intent.Update, projectId)
  const canTrigger = hasPermission(Permissions.Project.Workflow.Trigger, projectId)
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const { data: intentDetail, isLoading } = useIntent(projectId, open && intent ? intent.id : null)
  const [triggerPickerOpen, setTriggerPickerOpen] = useState(false)
  const [triggerDefinition, setTriggerDefinition] = useState<TriggerableDefinition | null>(null)

  const { data: attachmentsData } = useIntentAttachments(
    projectId,
    intent?.id ?? '',
  )

  const activeItemKey = open ? intent?.id ?? null : null
  const [prevItemKey, setPrevItemKey] = useState<string | null>(null)
  if (activeItemKey !== prevItemKey) {
    setPrevItemKey(activeItemKey)
    if (activeItemKey) {
      setActiveTab('overview')
    }
  }

  if (!intent) return null

  const attachmentCount = attachmentsData?.totalCount ?? 0

  const tabs = baseTabs.map(t => ({
    ...t,
    badge: t.key === 'attachments' ? attachmentCount : undefined,
  }))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] flex flex-col p-0 rounded-none border-none gap-0">
        <DialogHeader className="px-8 py-4 border-b shrink-0 bg-background">
          <DialogTitle className="text-xl font-bold">
            Intent #{String(intent.number).padStart(3, '0')} - {intent.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto bg-input-background">
          <div className="sticky top-0 z-10 bg-input-background">
            <div className="max-w-[1100px] w-full mx-auto px-6">
              <div className="flex items-center justify-between py-3">
                <TabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} className="bg-background" />

                <div className="flex items-center gap-2">
                  {activeTab === 'overview' && onEdit && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="iconLg"
                      className="hover:shadow-sm"
                      onClick={onEdit}
                      aria-label="Edit intent"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}

                  {activeTab === 'attachments' && canEdit && (
                    <Button
                      type="button"
                      onClick={() => {
                        const dropZone = document.querySelector<HTMLElement>('[data-testid="intent-attachments-drop-zone"]')
                        dropZone?.click()
                      }}
                      aria-label="Add attachments"
                      data-testid="add-intent-attachments-button"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      New Attachments
                    </Button>
                  )}

                  {activeTab === 'workflow-history' && canTrigger && (
                    <Button
                      type="button"
                      onClick={() => setTriggerPickerOpen(true)}
                      aria-label="Trigger workflow"
                      data-testid="trigger-workflow-from-history-button"
                    >
                      <MotionPlayIcon className="h-4 w-4 mr-1" />
                      Trigger
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-[1100px] mx-auto px-6">
              {activeTab === 'overview' && (
                <Card className="p-8 mb-8 overflow-hidden break-words" data-testid="intent-detail-overview">
                  <h2 className="text-2xl font-medium mb-6 text-card-foreground">Overview</h2>

                  <div className="grid grid-cols-4 gap-6 mb-8">
                    {intent.epic && (
                      <div>
                        <p className="text-xs font-semibold">Epic</p>
                        <p className="text-sm text-muted-foreground">{intent.epic}</p>
                      </div>
                    )}
                    {intent.feature && (
                      <div>
                        <p className="text-xs font-semibold">Feature</p>
                        <p className="text-sm text-muted-foreground">{intent.feature}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-semibold">Status</p>
                      <WorkItemStatusBadge status={intent.status} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold">Created</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(intent.createdAt).toLocaleDateString('sv-SE')}
                      </p>
                    </div>
                  </div>

                  <hr className="mb-8" />

                  <h2 className="text-2xl font-medium mb-4 text-card-foreground">Description</h2>

                  <div data-testid="intent-detail-description">
                    {isLoading ? (
                      <div className="flex items-center justify-center py-12" role="status" aria-live="polite" data-testid="intent-detail-loading">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <ScrollableContent maxHeight={FIELD_MAX_HEIGHT} className="text-sm">
                        <MarkdownViewer content={intentDetail?.description ?? ''} />
                      </ScrollableContent>
                    )}
                  </div>
                </Card>
              )}

              {activeTab === 'attachments' && (
                <Card className="p-8 mb-8">
                  <IntentAttachmentsTab projectId={projectId} intentId={intent.id} canEdit={canEdit} />
                </Card>
              )}

              {activeTab === 'workflow-history' && (
                <Card className="p-8 mb-8">
                  <WorkflowHistoryTab
                    projectId={projectId}
                    workItemId={intent.id}
                  />
                </Card>
              )}
            </div>
        </div>

        <TriggerWorkflowFromEntityDialog
          projectId={projectId}
          open={triggerPickerOpen}
          onOpenChange={setTriggerPickerOpen}
          onSelect={(def) => setTriggerDefinition(def)}
          linkedEntityType="Intent"
          linkedEntityId={intent.id}
        />

        <TriggerWorkflowDialog
          projectId={projectId}
          definition={triggerDefinition}
          open={triggerDefinition !== null}
          onOpenChange={(o) => { if (!o) setTriggerDefinition(null) }}
          extraInputValues={{ workItemId: intent.id }}
          linkedEntityId={intent.id}
        />
      </DialogContent>
    </Dialog>
  )
}
